import { inject, Injectable } from '@angular/core';
import { NotificationService } from 'src/app/core/services/notification.service';
import { TranslocoService } from '@jsverse/transloco';
import { Trip } from '../../trip-calculator/models/trip';
import { DocumentAnalysisJob } from '../../trip-calculator/models/trip';

/**
 * Service to handle brochure download functionality
 * Provides reusable methods for downloading AI-uploaded brochures with their original filenames
 */
@Injectable({
  providedIn: 'root',
})
export class BrochureDownloadService {
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);
  /**
   * Check if a trip has any downloadable brochures
   * @param trip - The trip object to check
   * @returns true if the trip has at least one brochure with a valid s3Url
   */
  hasBrochures(trip: Trip | null | undefined): boolean {
    const brochures = this.getBrochures(trip);
    return brochures.length > 0;
  }

  /**
   * Get the list of brochures linked to a trip
   * Filters out entries without a valid originalFileName
   * @param trip - The trip object
   * @returns Array of DocumentAnalysisJob objects with a valid filename
   */
  getBrochures(trip: Trip | null | undefined): DocumentAnalysisJob[] {
    const brochures = trip?.documentAnalysisJob ?? [];
    return brochures.filter((doc) => doc.originalFileName && doc.originalFileName.trim() !== '');
  }

  /**
   * Truncate a filename if it's too long, preserving the extension
   * @param filename - The filename to truncate
   * @param maxLength - Maximum length (default: 30 characters)
   * @returns Truncated filename with ellipsis if needed
   */
  truncateFilename(filename: string, maxLength: number = 30): string {
    if (filename.length <= maxLength) {
      return filename;
    }

    // Extract extension
    const lastDotIndex = filename.lastIndexOf('.');
    const extension = lastDotIndex !== -1 ? filename.substring(lastDotIndex) : '';
    const nameWithoutExt = lastDotIndex !== -1 ? filename.substring(0, lastDotIndex) : filename;

    // Calculate how many characters we can keep from the name
    const availableLength = maxLength - extension.length - 3; // 3 for '...'

    if (availableLength <= 0) {
      // If extension is too long, just truncate the whole thing
      return filename.substring(0, maxLength - 3) + '...';
    }

    return nameWithoutExt.substring(0, availableLength) + '...' + extension;
  }

  /**
   * Download a brochure with its original filename
   * Uses fetch + blob to force download with correct name (bypasses CORS download attribute limitation)
   *
   * @param s3Url - The S3 URL of the brochure
   * @param originalFileName - The original filename to use for download
   */
  async downloadBrochure(
    s3Url: string,
    originalFileName: string
  ): Promise<void> {
    try {
      const response = await fetch(s3Url);
      if (!response.ok) {
        throw new Error(
          `Download failed with status ${response.status}: ${response.statusText}`
        );
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading brochure:', error);

      // Show error message to user
      this.notificationService.error(
        this.translocoService.translate('brochure.downloadErrorDetail'),
      );

      // Fallback: open in new tab if download fails
      window.open(s3Url, '_blank');
    }
  }
}
